import { watch } from "vue";
import { useSessionStorage } from "@allwyn/storage/composables/use-storage";
import { ResponsiveUtils } from "@allwyn/utils/responsive-utils";
import { useChat } from "./composables/use-chat";
import { useFixedHelpState } from "./composables/use-fixed-help-state";

export interface FixedHelpRegistrationConfiguration {
    disableChatAlerts: boolean;
    startChatAlertAfterSeconds: number;
    stopChatAlertAfterSeconds: number;
}

export const useFixedHelp = (registrationConfig?: FixedHelpRegistrationConfiguration) => {
    const elem = document.getElementById("fixed-help");
    if (!elem) return;

    const { isFixedHelpCollapsed } = useFixedHelpState();

    bindClickToStartChat();
    fixedHelpCobrowsingModeCheck();
    const isNativeRegistration = !!registrationConfig;
    const isFixedHelpHiddenByDefault = isNativeRegistration && ResponsiveUtils.breakpointDown("sm2");

    const switchElem = document.getElementById("switch-help");
    switchElem.addEventListener("click", () => {
        isFixedHelpCollapsed.value = !isFixedHelpCollapsed.value;
    });
    const animation = isNativeRegistration
        ? useFixedHelpAnimation(elem, switchElem, isFixedHelpHiddenByDefault, registrationConfig)
        : null;

    watch(isFixedHelpCollapsed, (newIsFixedHelpCollapsed) => {
        if (newIsFixedHelpCollapsed) {
            elem.classList.remove("active");
            elem.querySelector(".fixed-help__switch")?.setAttribute("aria-label", "Možnosti chatu");
            animation?.stop(true);
        } else {
            elem.classList.add("active");
            elem.querySelector(".fixed-help__switch")?.setAttribute("aria-label", "Zavřít chat");
            animation?.start();
        }
    });

    if (!isFixedHelpCollapsed.value && !isFixedHelpHiddenByDefault) {
        elem.classList.add("active");
    }

    if (isNativeRegistration) {
        animation.start();
    }
};

export function fixedHelpCobrowsingMode() {
    document.getElementById("fixed-help")?.classList?.add("fixed-help--cobrowsing");
    useSessionStorage().setItem("fixed-help--cobromode", "true", "essential");
}

function fixedHelpCobrowsingModeCheck() {
    if (useSessionStorage().getItem("fixed-help--cobromode") === "true") {
        fixedHelpCobrowsingMode();
    }
}

function useFixedHelpAnimation(
    rootElem: HTMLElement,
    switchElem: HTMLElement,
    isFixedHelpHiddenByDefault: boolean,
    registrationConfig?: FixedHelpRegistrationConfiguration
) {
    const ANIMATION_DURATION = 2000;
    const ACTIVE_CLASS_DELAY = 200;

    const elemAnimate = rootElem.querySelector(".fixed-help__inner");
    let startAnimationTimer = null;
    let stopAnimationTimer = null;
    let animationInterval = null;
    let isAnimating = false;
    let shouldHideOnEnd = false;
    let didChatOpen = false;

    function handleActiveClassAddition() {
        if (elemAnimate.classList.contains("is-animating-out") && !rootElem.classList.contains("active")) {
            rootElem.classList.add("active");
        }
    }

    function animateOut() {
        elemAnimate.classList.remove("is-animating-in");
        elemAnimate.classList.add("is-animating-out");
        switchElem.classList.remove("is-animating-in");
        switchElem.classList.add("is-animating-out");
        setTimeout(handleActiveClassAddition, ACTIVE_CLASS_DELAY);
    }

    function animateIn() {
        elemAnimate.classList.add("is-animating-in");
        elemAnimate.classList.remove("is-animating-out");
        switchElem.classList.add("is-animating-in");
        switchElem.classList.remove("is-animating-out");
    }

    function toggleAnimation() {
        if (elemAnimate.classList.contains("is-animating-in")) {
            animateOut();
        } else {
            animateIn();
        }
    }

    function openProactiveChat() {
        didChatOpen = true;
        !rootElem.classList.contains("active") && rootElem.classList.add("active");
        if (window.showChat) {
            window.showChat({
                isProactiveChat: true,
                campaignCode: "DEHRegTimeout",
                creativeCode: "none",
            });
        } else {
            useChat().show(true, "DEHRegTimeout", "none");
        }
    }

    function startFlow() {
        if (registrationConfig?.disableChatAlerts ?? true) return;

        clearTimeout(startAnimationTimer);
        startAnimationTimer = setTimeout(() => {
            if (isFixedHelpHiddenByDefault) {
                rootElem.classList.add("active");
                shouldHideOnEnd = true;
                isAnimating = true;
                animationInterval = setInterval(toggleAnimation, ANIMATION_DURATION);

                stopAnimationTimer = setTimeout(() => {
                    stopAnimation();
                }, registrationConfig?.stopChatAlertAfterSeconds * 1000);
            } else if (!didChatOpen) {
                openProactiveChat();
            }
        }, registrationConfig?.startChatAlertAfterSeconds * 1000);
    }

    function stopAnimation(forceHide = false) {
        clearInterval(animationInterval);
        clearTimeout(stopAnimationTimer);

        if (forceHide) shouldHideOnEnd = false;
        if (shouldHideOnEnd) rootElem.classList.remove("active");

        if (isAnimating) {
            isAnimating = false;
            elemAnimate.classList.remove("is-animating-in");
            elemAnimate.classList.add("is-animating-out");
            switchElem.classList.remove("is-animating-in");
            switchElem.classList.add("is-animating-out");
        }
    }

    function restartFlow() {
        stopAnimation();
        startFlow();
    }

    document.addEventListener("click", (e) => {
        if (!(e.target as HTMLElement).closest("#fixed-help")) {
            restartFlow();
        }
    });

    document.addEventListener("keydown", () => restartFlow());

    return {
        start: startFlow,
        stop: stopAnimation,
    };
}
function bindClickToStartChat() {
    const btns = document.querySelectorAll<HTMLButtonElement>("[data-start=chat]");
    btns.forEach((btn) => {
        btn.addEventListener("click", (e) => {
            const campaingCode = (e.currentTarget as HTMLButtonElement).dataset.campaing;
            if (window.showChat) {
                window.showChat({ campaignCode: campaingCode });
            } else {
                useChat().show(false, campaingCode);
            }
        });
    });
}
